import { Injectable, inject } from '@angular/core';
import { DirtyCheckPlugin, QueryEntity } from '@datorama/akita';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import { ApplicationInfoModel } from '../../cmg-1003/models';
import { IdValue } from '../../shared/models';
import { truncateEllipses } from '../../shared/utils';
import { Cmg1003BorrowerQuery } from '../borrower/borrower.query';
import { Cmg1003ApplicationInfoState, Cmg1003ApplicationInfoStore } from './application-info.store';

@Injectable({ providedIn: 'root' })
export class Cmg1003ApplicationInfoQuery extends QueryEntity<Cmg1003ApplicationInfoState> {
  protected store: Cmg1003ApplicationInfoStore;
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);

  static instance: Cmg1003ApplicationInfoQuery;
  dirtyChecker = new DirtyCheckPlugin(this);
  readonly emptyFirstLastName = '______'; // TODO: Figure out what is a good value here
  constructor() {
    const store = inject(Cmg1003ApplicationInfoStore);

    super(store);
    this.store = store;

    Cmg1003ApplicationInfoQuery.instance = this;
  }
  getAllApplications() {
    const apps$ = this.selectAll().pipe(filter((appInfos) => appInfos.length > 0));
    const borrowers$ = this._borrowerQuery.selectAll();

    return combineLatest([apps$, borrowers$]).pipe(
      map(([applications, borrowers]) => {
        return applications.length > 0 && borrowers.length > 0
          ? applications.map((t) => {
              // sort, so that primary borrower is first
              borrowers.sort((a, b) => Number(b.isPrimaryBorrower) - Number(a.isPrimaryBorrower));
              const borrowerIds = [t.borrowerId];
              if ((CmgUiUtilities.isIdTemporary(t.coBorrowerId) && t.additionalBorrowerChecked) || !CmgUiUtilities.isIdTemporary(t.coBorrowerId)) {
                borrowerIds.push(t.coBorrowerId);
              }
              const borrowerCN = borrowers
                .filter((b) => b !== null && borrowerIds.some((j) => j === b.borrowerId))
                .map((b) => {
                  if (CmgUiUtilities.isIdTemporary(t.applicationId)) {
                    return `${b.firstName ?? this.emptyFirstLastName} ${b.lastName ?? this.emptyFirstLastName}`;
                  } else {
                    return `${b.firstName ?? this.emptyFirstLastName} ${b.lastName ?? this.emptyFirstLastName}`;
                  }
                });
              return new IdValue(
                t.applicationId,
                borrowerCN.reduce((a, c, i) => a + (i > 0 ? ' | ' : '') + c, ''),
                this._formatApplicationString(t, borrowerCN),
              );
            })
          : [];
      }),
    );
  }
  /**
   * Note: Additional borrower may not always be present. The app still displays
   * the form for additional borrower. This function helps determine the different states of that.
   * @returns
   */
  isAdditionalBorrowerPresent(applicationId: number) {
    const application = this.getEntity(applicationId);
    return (
      (CmgUiUtilities.isIdTemporary(application.coBorrowerId) && application.additionalBorrowerChecked) ||
      !CmgUiUtilities.isIdTemporary(application.coBorrowerId)
    );
  }

  /**
   * Returns coBorrowerId if additional borrower exists, else null.
   * @returns
   */
  getAdditionalBorrowerId$() {
    return this.selectActive().pipe(
      filter((t) => !!t),
      switchMap((application) => {
        return of(
          (CmgUiUtilities.isIdTemporary(application.coBorrowerId) && application.additionalBorrowerChecked) ||
            !CmgUiUtilities.isIdTemporary(application.coBorrowerId)
            ? application.coBorrowerId
            : null,
        );
      }),
    );
  }

  isPrimaryBorrower(borrowerId: number): boolean {
    const appWithBorrower = this.getAll({
      filterBy: (t) => t.borrowerId === borrowerId || t.coBorrowerId === borrowerId,
      limitTo: 1,
    });
    return appWithBorrower.length > 0 && appWithBorrower[0] && appWithBorrower[0].borrowerId === borrowerId;
  }

  /**
   * Returns formatted string for Borrower Name(s) used in application selection dropdowns
   * @param application Application Model
   * @param borrowers String list of Borrower Names
   * @returns
   */
  private _formatApplicationString(application: ApplicationInfoModel, borrowers: string[]): string {
    const maxNameLength = 18;

    if (CmgUiUtilities.isIdTemporary(application.applicationId)) {
      return 'New Application';
    } else {
      switch (borrowers?.length) {
        case 1: {
          return truncateEllipses(borrowers[0], maxNameLength * 2);
        }
        case 2: {
          return truncateEllipses(borrowers[0], maxNameLength) + ' | ' + truncateEllipses(borrowers[1], maxNameLength);
        }
        default: {
          return borrowers.reduce((a, c, i) => a + (i > 0 ? ' | ' : '') + c, '');
        }
      }
    }
  }
}
